"use client";

import { useEffect, useRef } from "react";

export default function IntroSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = entry.target.querySelectorAll(".reveal.hidden");
            reveals.forEach((el, i) => {
              setTimeout(() => {
                el.classList.remove("hidden");
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="introduction"
      ref={sectionRef}
      className="py-28 md:py-40 px-6 md:px-16 bg-cream"
    >
      <div className="max-w-8xl mx-auto">
        {/* Section header */}
        <div className="flex items-center gap-6 mb-16 reveal hidden">
          <span
            className="font-serif italic text-sm"
            style={{ color: "var(--sage)" }}
          >
            Page 02
          </span>
          <div className="gold-line" />
          <span
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: "var(--warm-gray)" }}
          >
            Introduction
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* Left: Big title */}
          <div className="lg:col-span-5 reveal hidden">
            <h2
              className="font-serif italic leading-tight"
              style={{ fontSize: "clamp(2.5rem, 5vw, 4rem)", color: "var(--charcoal)" }}
            >
              Pourquoi ce guide{" "}
              <span className="not-italic shimmer-text">existe.</span>
            </h2>
          </div>

          {/* Right: Content */}
          <div className="lg:col-span-7 space-y-8">
            <p
              className="text-base leading-relaxed reveal hidden delay-100"
              style={{ color: "var(--warm-gray)" }}
            >
              Vous avez commencé avec 3 logements. Puis 5. Puis 8. Et maintenant vous gérez plus de 10 biens — et quelque chose a changé. Ce qui fonctionnait avant ne fonctionne plus aussi bien. Les voyageurs posent des questions auxquelles vous n'avez pas toujours le temps de répondre. Les équipes de ménage manquent parfois d'instructions précises. Et les avis 5 étoiles deviennent moins systématiques.
            </p>

            <p
              className="text-base leading-relaxed reveal hidden delay-200"
              style={{ color: "var(--warm-gray)" }}
            >
              Ce guide est né de centaines d'échanges avec des gestionnaires de conciergeries en croissance. Il ne s'agit pas d'une théorie. C'est un retour d'expérience structuré de professionnels qui ont traversé ce cap difficile — et qui en sont sortis plus forts.
            </p>

            {/* Quote */}
            <blockquote
              className="editorial-quote reveal hidden delay-300"
              style={{ marginTop: "2rem" }}
            >
              <p
                className="font-serif italic text-xl leading-relaxed"
                style={{ color: "var(--charcoal)" }}
              >
                "À partir de 10 logements, l'improvisation devient votre plus grand risque opérationnel."
              </p>
              <footer
                className="mt-4 text-xs font-bold uppercase tracking-widest"
                style={{ color: "var(--sage)" }}
              >
                — Constat terrain, conciergeries 2024-2026
              </footer>
            </blockquote>

            {/* What you'll learn */}
            <div
              className="reveal hidden delay-400 rounded-2xl p-8 mt-8"
              style={{ background: "var(--ivory)", border: "1px solid var(--border)" }}
            >
              <h3
                className="text-xs font-bold uppercase tracking-widest mb-6"
                style={{ color: "var(--sage)" }}
              >
                Ce que vous allez apprendre
              </h3>
              <ul className="space-y-3">
                {[
                  "Identifier le plafond invisible qui bloque votre croissance",
                  "Standardiser vos process pour chaque logement",
                  "Centraliser l'information pour toute votre équipe",
                  "Automatiser les communications répétitives",
                  "Piloter la qualité avec des KPIs concrets",
                  "Évaluer votre niveau de maturité opérationnelle",
                ]?.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm" style={{ color: "var(--charcoal)" }}>
                    <span
                      className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: "var(--sage)", color: "white", fontSize: "10px", fontWeight: 700 }}
                    >
                      {i + 1}
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}